import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

const amplitudeMock = {
  setCommonProperty: () => {},
  removeCommonProperty: () => {},
  track: () => Promise.resolve(),
};

export default defineNuxtPlugin(async(_context) => {
  const { $auth, $config: { public: { AMPLITUDE_API_KEY } }, provide } = useNuxtApp();
  const commonProps = {
    on_version: null,
    client_id: null,
    platform_os: detectPlatformOs(),
  };

  if (typeof AMPLITUDE_API_KEY !== 'string' || AMPLITUDE_API_KEY.length < 5) {
    console.warn('Amplitude API Key not provided. Amplitude won\'t be initialized.');

    provide('analytics', amplitudeMock);
    return;
  }

  const initAmplitude = async() => {
    const user = $auth?.value?.user?.profile;
    const isImpersonating = (user?.impersonated_nav_info as Dict<boolean>)?.show;
    const userId = isImpersonating
      ? (user?.pendo_setup as Dict<Dict<string>>)?.visitor?.id
      : user?.sub?.split(':').at(-1);

    // default attrs to be sent
    commonProps.on_version = null;
    commonProps.client_id = user?.client as string;

    const sampleRate =
    process.env.NODE_ENV === 'production'
    ? 1 // It represents 100%
    : 0.1; // It represents 10%
    const sessionReplayTracking = sessionReplayPlugin({ sampleRate });
    amplitude.add(sessionReplayTracking);

    amplitude.init(AMPLITUDE_API_KEY, userId, {
      defaultTracking: true,
      sessions: true,
      autocapture: {
        elementInteractions: true,
      },
    });

    // Add User properties
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('client_id', user?.client as string);
    amplitude.identify(identifyEvent);
  };

  // Common props are going to be sent with every single amplitude event
  provide('analytics', {
    setCommonProperty: (name, value) => { commonProps[name] = value },
    removeCommonProperty: (name: keyof typeof commonProps) => { commonProps[name] = null; },
    track: (eventName, eventProps) => withTimeout(amplitude.track(eventName, { ...commonProps, ...eventProps })?.promise),
  });

  await initAmplitude();
})
